<template>
    <div>
        <vrm-header />
        <vrm-title>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span>
                        {{
                            $t('nav.release_details', {
                                i18nFallback: 'Release details for: {releaseTitle}',
                                releaseTitle: releaseTitle,
                            })
                        }}
                    </span>
                </h1>
            </template>
        </vrm-title>
        <div class="container">
            <div
                v-html="
                    $t('nav.release_details_description', {
                        i18nFallback:
                            'Release details includes version details, changes or updates, and any relevant firmware files.',
                    })
                "
            ></div>
            <br />
            <form class="vrm-form vrm-form__create-new-release">
                <span v-if="!showWizard && (showEditSuccess || showDeleteSuccess)" class="fragment">
                    <div class="dynamic-landing">
                        <div class="dynamic-landing__centered">
                            <img src="/img/svg-icons/success.svg" alt="success" />
                            <br />
                        </div>
                        <h1 class="dynamic-landing__centered dynamic-landing__centered__no-margin">
                            {{
                                showEditSuccess
                                    ? $t('firmware-library.releaseEditSuccessful', {
                                          i18nFallback: 'Release edit successful!',
                                      })
                                    : $t('firmware-library.releaseDeleteSuccessful', {
                                          i18nFallback: 'Release delete successful!',
                                      })
                            }}
                        </h1>
                        <div class="dynamic-landing__centered">
                            <p v-if="showEditSuccess">
                                {{
                                    $t('firmware-library.releaseEditComplete', {
                                        i18nFallback:
                                            'The release was edited successfully. See full details in the Release Overview page.',
                                    })
                                }}
                            </p>
                            <br />

                            <div class="dynamic-landing__buttons">
                                <router-link
                                    :to="{
                                        name: 'base.release-overview',
                                    }"
                                >
                                    <button class="sg-btn sg-blue">
                                        {{
                                            $t('firmware-library.goToOverview', {
                                                i18nFallback: 'Go to release overview',
                                            })
                                        }}
                                    </button>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </span>
                <span v-else-if="showWizard" class="fragment">
                    <wizard
                        :value="currentId"
                        :steps="getSteps"
                        :is-edit="true"
                        :completed-until="completedUntil"
                        :can-submit="isFormValid()"
                        :next-disabled="isStepValid()"
                        :force-back-disabled="currentId === STEPS.FILES"
                        :has-change="hasChange"
                        :creating="false"
                        :submit-text="
                            $t('firmware-library.editRelease', { i18nFallback: 'Edit release' })
                        "
                        :has-overview="false"
                        @input="setCurrentId"
                        @submit="onSubmit"
                        @submitAndReset="submitAndReset"
                    >
                        <files
                            v-if="currentId === STEPS.FILES"
                            :initial-values="initialForm"
                            :is-edit="true"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <properties
                            v-if="currentId === STEPS.PROPERTIES"
                            :initial-values="initialForm"
                            :is-edit="true"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <info
                            v-if="currentId === STEPS.INFO"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                    </wizard>
                </span>
            </form>
            <div v-if="showWizard" class="sg-btn__container sg-btn__container--align-right">
                <button type="button" class="sg-btn sg-red" @click.prevent="onDelete">
                    {{ $t('firmware-library.deleteRelease', { i18nFallback: 'Delete release' }) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmHeader from '@/vue/components/layout/VrmHeader.vue';
import VrmTitle from '@/vue/components/layout/VrmTitle.vue';
import Wizard from '@/vue/components/pages/dynamic-ess/wizard/Wizard.vue';
import Files from './forms/Files.vue';
import Properties from './forms/Properties.vue';
import Info from './forms/Info.vue';
import {
    CREATE_NEW_RELEASE_FORM_FIELDS,
    createForm,
    applyReleaseDetailData,
    NEW_RELEASE,
    resetForm,
} from '@/vue/store/modules/create-new-release-form';
import FirmwareLibraryFileIssuesModal from '@/vue/components/modals/FirmwareLibraryFileIssuesModal.vue';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal.vue';
@Component({
    components: {
        Wizard,
        VrmHeader,
        VrmTitle,
        Files,
        Properties,
        Info,
        FirmwareLibraryFileIssuesModal,
    },
})
@AngularRoute('base.edit-release', '/edit-release/{idRelease:int}', { slot: 'main' })
@AngularLocaleBundle('nav')
@AngularLocaleBundle('firmware-library')
export default class VrmFirmwareReleaseEdit extends Vue {
    /** @type VrmModalService */
    @ngInject()
    VrmModalService;
    /** @type FirmwareLibraryService */
    @ngInject()
    FirmwareLibraryService;
    /** @type ApiService */
    @ngInject()
    ApiService;

    showWizard = true;
    showEditSuccess = false;
    showDeleteSuccess = false;
    STEPS = {
        FILES: 'files',
        PROPERTIES: 'properties',
        INFO: 'info',
    };
    currentId = this.STEPS.FILES;
    initialForm = createForm();
    initialFiles = [];

    hasChange = false;
    formChanges = {
        files: false,
        properties: false,
    };

    get idRelease() {
        return this.$route.params.idRelease;
    }

    get releaseTitle() {
        const title = this.$store.state.createNewReleaseForm.title;
        return title ? title : 'Release ' + this.idRelease;
    }

    async mounted() {
        const data = await this.FirmwareLibraryService.getReleaseData(this.idRelease);
        this.$store.commit(NEW_RELEASE.SET_FORM_WITH_RELEASE_DETAILS, data);
        applyReleaseDetailData(this.initialForm, data);
        this.initialFiles = [...data.firmwares];
    }

    beforeDestroy() {
        this.$store.commit(NEW_RELEASE.RESET_FORM);
        resetForm(this.initialForm);
    }

    get completedUntil() {
        let stepId = this.currentId;
        for (const step of this.getSteps) {
            const isValid = step.validator ? this.$store.getters[step.validator] : true;
            if (!isValid) {
                break;
            }
            stepId = step.id;
        }
        return this.getSteps.findIndex(i => i.id === stepId) + 1;
    }

    async setCurrentId(newId) {
        for (const step of this.getSteps) {
            const isValid = step.validator ? this.$store.getters[step.validator] : true;
            if (step.id === newId) {
                this.currentId = newId;
                return;
            }
            if (!isValid) {
                return;
            }
        }
    }

    get getSteps() {
        return [
            {
                id: this.STEPS.FILES,
                title: this.$t('firmware-library.files', { i18nFallback: 'Upload file(s)' }),
                validator: NEW_RELEASE.FILES_VALID,
            },
            {
                id: this.STEPS.PROPERTIES,
                title: this.$t('firmware-library.properties', {
                    i18nFallback: 'Firmware properties',
                }),
                validator: NEW_RELEASE.PROPERTIES_VALID,
            },
            {
                id: this.STEPS.INFO,
                title: this.$t('firmware-library.info', { i18nFallback: 'Release info' }),
            },
        ].filter(Boolean);
    }

    isStepValid() {
        if (this.currentId === this.STEPS.FILES) {
            return !this.$store.getters[NEW_RELEASE.FILES_VALID];
        }
        if (this.currentId === this.STEPS.PROPERTIES) {
            return !this.$store.getters[NEW_RELEASE.PROPERTIES_VALID];
        }
        if (this.currentId === this.STEPS.INFO) {
            return false;
        }
        return false;
    }

    isFormValid() {
        return this.currentId === this.STEPS.INFO && this.$store.getters[NEW_RELEASE.FORM_VALID];
    }

    handleChange(value) {
        this.formChanges[value.formName] = value.hasChange;
        this.hasChange = this.formChanges.files || this.formChanges.properties;
    }

    handleCreate(value) {
        this.hasChange = value;
    }

    submitAndReset() {
        this.onSubmit();
        this.resetWizard();
    }

    onSubmit() {
        const data = this.$store.getters[NEW_RELEASE.TO_RELEASE_EDIT](
            this.idRelease,
            this.initialFiles,
        );
        this.ApiService.editFirmwareRelease(data).then(response => {
            this.showWizard = false;
            if (response.data && response.data.success) {
                this.showEditSuccess = true;
            }
        });
    }

    get files() {
        return this.$store.state.createNewReleaseForm.files;
    }

    set files(value) {
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: value,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.files,
        });
    }

    async onDelete() {
        if (this.busy) return;
        this.busy = true;
        const props = {
            item: this.$t('firmware-library.confirm_deletion_item', { i18nFallback: 'release' }),
            message: this.$t('firmware-library.confirm_deletion_message', {
                i18nFallback: 'Deleting release will create a new release that deletes the file.',
            }),
        };
        if (await this.VrmModalService.openVueModal(ConfirmDeletionModal, props)) {
            this.ApiService.deleteFirmwareRelease(this.idRelease).then(response => {
                this.showWizard = false;
                if (response.data && response.data.success) {
                    this.showDeleteSuccess = true;
                }
            });
        } else {
            this.busy = false;
        }
    }
}
</script>
